import { getFeedbackIcon } from '@/@admin/utils';
import { STUDIO_AI_NAME } from '@/@studio-ai/constants';
import { UserFeedback } from '@/@studio-ai/types';
import { formatDateTime } from '@/lib/utils/formatting';
import { Button, Chip, cn, Tooltip } from '@heroui/react';
import { Icon } from '@iconify/react';

export function FeedbackCard({
    feedback: fb,
    expandedFeedback,
    expandedAiMessage,
    onToggleFeedback,
    onToggleAiMessage,
    onMarkAsRead,
    onOpenConversation,
}: {
    feedback: UserFeedback;
    expandedFeedback: number | null;
    expandedAiMessage: number | null;
    onToggleFeedback: (id: number) => void;
    onToggleAiMessage: (id: number) => void;
    onMarkAsRead: (id: number) => void;
    onOpenConversation: (id: string) => void;
}) {
    const hasUnreadResponse = !fb.isRead && !!fb.response;

    return (
        <div
            className={cn(
                'group/card px-4 py-3.5 transition-colors',
                hasUnreadResponse ? 'bg-primary-50/30' : 'hover:bg-default-50',
            )}
        >
            {/* Header row */}
            <div className="flex items-start gap-3">
                <Tooltip
                    content={fb.isPositive ? 'Positive feedback' : 'Negative feedback'}
                    size="sm"
                >
                    <div
                        className={cn(
                            'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg',
                            fb.isPositive
                                ? 'bg-success-100 text-success-600'
                                : 'bg-danger-100 text-danger-600',
                        )}
                    >
                        <Icon icon={getFeedbackIcon(fb.isPositive)} height={14} width={14} />
                    </div>
                </Tooltip>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        {fb.category && (
                            <Chip
                                size="sm"
                                variant="flat"
                                className="h-5 px-2 text-[10px] font-medium"
                            >
                                {fb.category}
                            </Chip>
                        )}
                        {hasUnreadResponse && (
                            <Chip
                                size="sm"
                                color="primary"
                                variant="dot"
                                className="h-5 border-none px-1.5 text-[10px]"
                            >
                                New response
                            </Chip>
                        )}
                        <span className="ml-auto shrink-0 text-mini text-default-400">
                            {formatDateTime(fb.createdTimestamp)}
                        </span>
                    </div>
                    <p className="mt-1.5 text-small leading-relaxed text-default-700">
                        {fb.feedbackMessage}
                    </p>
                </div>
            </div>

            {/* AI Message */}
            {fb.aiMessage && (
                <div className="ml-10 mt-2.5">
                    <button
                        type="button"
                        className="flex w-full items-center gap-2 rounded-lg border border-default-100 bg-default-50/50 px-3 py-2 text-left transition-colors hover:bg-default-100/50"
                        onClick={() => onToggleAiMessage(fb.id)}
                    >
                        <Icon icon="lucide:bot" width={13} className="shrink-0 text-default-400" />
                        <span className="min-w-0 flex-1 text-mini font-medium text-default-500">
                            {STUDIO_AI_NAME} Response Reviewed
                        </span>
                        <Icon
                            icon={
                                expandedAiMessage === fb.id
                                    ? 'lucide:chevron-up'
                                    : 'lucide:chevron-down'
                            }
                            width={12}
                            className="shrink-0 text-default-400"
                        />
                    </button>
                    {expandedAiMessage === fb.id && (
                        <div className="mt-1.5 rounded-lg border border-default-100 bg-default-50/30 px-3 py-2.5">
                            <p className="text-small leading-relaxed text-default-600">
                                {fb.aiMessage}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Admin Response */}
            {fb.response && (
                <div className="ml-10 mt-2.5">
                    <button
                        type="button"
                        className={cn(
                            'flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors',
                            hasUnreadResponse
                                ? 'border-primary-200 bg-primary-50/50 hover:bg-primary-100/50'
                                : 'border-default-100 bg-default-50/50 hover:bg-default-100/50',
                        )}
                        onClick={() => onToggleFeedback(fb.id)}
                    >
                        <Icon
                            icon="lucide:shield-check"
                            width={13}
                            className={cn(
                                'shrink-0',
                                hasUnreadResponse ? 'text-primary-500' : 'text-default-400',
                            )}
                        />
                        <span
                            className={cn(
                                'min-w-0 flex-1 text-mini font-medium',
                                hasUnreadResponse ? 'text-primary-600' : 'text-default-500',
                            )}
                        >
                            Admin Response
                        </span>
                        <Icon
                            icon={
                                expandedFeedback === fb.id
                                    ? 'lucide:chevron-up'
                                    : 'lucide:chevron-down'
                            }
                            width={12}
                            className={cn(
                                'shrink-0',
                                hasUnreadResponse ? 'text-primary-400' : 'text-default-400',
                            )}
                        />
                    </button>
                    {expandedFeedback === fb.id && (
                        <div
                            className={cn(
                                'mt-1.5 rounded-lg border px-3 py-2.5',
                                hasUnreadResponse
                                    ? 'border-primary-100 bg-primary-50/30'
                                    : 'border-default-100 bg-default-50/30',
                            )}
                        >
                            <p
                                className={cn(
                                    'text-small leading-relaxed',
                                    hasUnreadResponse ? 'text-primary-700' : 'text-default-600',
                                )}
                            >
                                {fb.response}
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Actions */}
            <div className="ml-10 mt-2.5 flex items-center gap-1">
                <Button
                    size="sm"
                    variant="light"
                    onPress={() => onOpenConversation(fb.conversationId)}
                    startContent={<Icon icon="lucide:external-link" width={13} />}
                    className="h-7 px-2 text-mini text-default-500 hover:text-default-700"
                >
                    View conversation
                </Button>
                {hasUnreadResponse && (
                    <Button
                        size="sm"
                        variant="light"
                        color="primary"
                        onPress={() => onMarkAsRead(fb.id)}
                        startContent={<Icon icon="lucide:check" width={13} />}
                        className="h-7 px-2 text-mini"
                    >
                        Mark as read
                    </Button>
                )}
            </div>
        </div>
    );
}
